import React, { useState } from 'react';
import { FaMapMarkerAlt, FaEnvelope } from 'react-icons/fa';
import { FaThreads } from 'react-icons/fa6';
import axios from 'axios';
import Botpoison from '@botpoison/browser';
import './Contact.css';
import './ConferenceRoomContact.css';
import IconWrapper from './IconWrapper';

// Create a new Botpoison instance with your public key
const botpoison = new Botpoison({
  publicKey: "pk_96c3b628-7a73-44f5-bb7f-ee58d3722f64"
});

const ConferenceRoomContact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    eventDate: '',
    startTime: '',
    duration: '',
    attendees: '',
    eventType: '',
    specialRequests: '',
    message: '',
    privacy: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.privacy) {
      alert('Please agree to the privacy policy to submit the form.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      // Submit to Formspark
      const response = await axios.post('https://submit-form.com/lJs7xi5or', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        eventDate: formData.eventDate,
        startTime: formData.startTime,
        duration: formData.duration,
        attendees: formData.attendees,
        eventType: formData.eventType,
        specialRequests: formData.specialRequests,
        message: formData.message,
        privacy: formData.privacy,
        _botpoison: solution, // Forward the Botpoison solution
        _subject: 'Conference Room Booking Request - The Collective at Gateway'
      });

      if (response.status === 200) {
        setSubmitMessage('Thank you! Your conference room booking request has been submitted successfully. We\'ll confirm availability and get back to you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          eventDate: '',
          startTime: '',
          duration: '',
          attendees: '',
          eventType: '',
          specialRequests: '',
          message: '',
          privacy: false
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage('There was an error submitting your booking request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="section-header">
          <h2>Book Conference Room</h2>
          <p>Reserve our professional conference room for your next meeting or event</p>
        </div>

        <div className="contact-grid">
          <div className="contact-form-container">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your Name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Your Email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Your Phone"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="company">Company/Organization *</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  placeholder="Your Company or Organization"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eventDate">Event Date *</label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    min={today}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="startTime">Start Time *</label>
                  <input
                    type="time"
                    id="startTime"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="duration">Duration *</label>
                  <select
                    id="duration"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Duration</option>
                    <option value="1-hour">1 Hour</option>
                    <option value="2-hours">2 Hours</option>
                    <option value="3-hours">3 Hours</option>
                    <option value="4-hours">4 Hours</option>
                    <option value="half-day">Half Day (4+ hours)</option>
                    <option value="full-day">Full Day (8+ hours)</option>
                    <option value="custom">Custom Duration</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="attendees">Number of Attendees *</label>
                  <select
                    id="attendees"
                    name="attendees"
                    value={formData.attendees}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    <option value="1-5">1-5 People</option>
                    <option value="6-10">6-10 People</option>
                    <option value="11-20">11-20 People</option>
                    <option value="21-35">21-35 People</option>
                    <option value="36-50">36-50 People</option>
                    <option value="51-67">51-67 People</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="eventType">Event Type *</label>
                <select
                  id="eventType"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Event Type</option>
                  <option value="team-meeting">Team Meeting</option>
                  <option value="client-presentation">Client Presentation</option>
                  <option value="strategy-session">Strategy Session</option>
                  <option value="workshop">Workshop</option>
                  <option value="training">Training Session</option>
                  <option value="company-offsite">Company Offsite</option>
                  <option value="interview">Interview/Hiring</option>
                  <option value="other">Other</option>
                </select>
              </div>

              

              <div className="form-group">
                <label htmlFor="specialRequests">Special Equipment/Setup Requests</label>
                <textarea
                  id="specialRequests"
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleChange}
                  rows={3}
                  placeholder="e.g., specific seating arrangement, additional AV equipment, whiteboards, etc."
                ></textarea>
              </div>

              <div className="form-group">
                <label htmlFor="message">Additional Comments</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Any additional details about your event or special requirements"
                ></textarea>
              </div>

              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="privacy"
                  name="privacy"
                  checked={formData.privacy}
                  onChange={handleCheckboxChange}
                  required
                />
                <label htmlFor="privacy">
                  I agree to the privacy policy and consent to being contacted about my booking request
                </label>
              </div>

              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting Request...' : 'Submit Booking Request'}
              </button>

              {submitMessage && (
                <div className={`submit-message ${submitMessage.includes('error') ? 'error' : 'success'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>

          <div className="contact-info">
            <div className="contact-card">
              <h3>Conference Room Details</h3>
              <div className="booking-info">
                <h4>Capacity</h4>
                <p>Up to 67 people</p>
                
                <h4>Booking Policy</h4>
                <p>• Advance reservation required</p>
                <p>• Professional use only</p>
                <p>• Cancellation policy applies</p>
                <p>• Setup/cleanup included</p>
              </div>

              <div className="info-item">
                <IconWrapper icon={FaMapMarkerAlt} />
                <div>
                  <h4>Address</h4>
                  <p>1810 Gateway Dr, San Mateo, CA 94404</p>
                </div>
              </div>

              <div className="info-item">
                <IconWrapper icon={FaEnvelope} />
                <div>
                  <h4>Email</h4>
                  <p><a href="mailto:smg@scpropsm.com">smg@scpropsm.com</a></p>
                </div>
              </div>

              <div className="info-item">
                <IconWrapper icon={FaThreads} />
                <div>
                  <h4>Website</h4>
                  <p><a href="https://www.scpropsm.com/" target="_blank" rel="noopener noreferrer">www.scpropsm.com</a></p>
                </div>
              </div>
            </div>

            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3164.0599391974257!2d-122.29784492392758!3d37.5429359272756!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808f9f3156c83f8f%3A0x1b9e2a5c0d2c1e1a!2s1810%20Gateway%20Dr%2C%20San%20Mateo%2C%20CA%2094404!5e0!3m2!1sen!2sus!4v1647882655590!5m2!1sen!2sus"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                title="The Collective at Gateway Location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConferenceRoomContact;
